import { createContext, FC, PropsWithChildren, useEffect, useState } from "react";
import { NavContextData } from "../@types/NavContextData";
import { useLocation, useParams } from "react-router-dom";
import { RouteType } from "../@types/RouteType";
import { useTour } from "@reactour/tour";
import useAuth from "../hooks/useAuth";
import { getMatchingRoute } from "../utils/getMatchingRoute";

export const NavContext = createContext<NavContextData | null>(null);

export const NavProvider: FC<PropsWithChildren> = ({ children }) => {
    const { user, userAuthorizedRoutes } = useAuth();
    const location = useLocation();
    const { id } = useParams();
    const [currentRoute, setCurrentRoute] = useState<RouteType | undefined>(undefined);
    const [routeDisplayText, setRouteDisplayText] = useState<string>('Dashboard');
    const [navExpanded, setNavExpanded] = useState<boolean>(true);
    const { setSteps } = useTour();

    /*
        Handle setting tour steps based on the current route
    */
    useEffect(() => {
        if (currentRoute && setSteps) {
            if (currentRoute.path === '/dashboard' && currentRoute.hasTutorial && currentRoute.tutorialSteps) {
                switch (user?.attributes.type) {
                    case 'dealer-sales':
                        setSteps([...currentRoute.tutorialSteps.slice(2, 2)]);
                        break;
                    case 'dealer-service':
                        const indexesToRemove = [1, 2, 6, 7]
                        const modifiedSteps = currentRoute.tutorialSteps
                            .filter((route, index) => indexesToRemove.indexOf(index) === -1);
                        setSteps(modifiedSteps);
                        break;
                    default:
                        setSteps(currentRoute.tutorialSteps);
                        break;
                }
            } else if (currentRoute.hasTutorial && currentRoute.tutorialSteps) {
                setSteps(currentRoute.tutorialSteps);
            } else {
                setSteps([]);
            }
        }
    }, [
        currentRoute,
        user,
        setSteps
    ]);

    useEffect(() => {
        
        // Scroll to top of page on route change
        window.scrollTo(0, 0);

        /*
            Check for an :id parameter on the route and if doesn't exist
            we can find the route and upodate the route display text
        */

        const dynamicRoute: boolean = id !== undefined;

        const matchingRoute: RouteType | undefined = getMatchingRoute(userAuthorizedRoutes, location, dynamicRoute);

        if (matchingRoute) {

            setCurrentRoute(matchingRoute);

            setRouteDisplayText(matchingRoute.displayText);

        }

    }, [
        user,
        location,
        userAuthorizedRoutes,
        id,
        setRouteDisplayText
    ]);

    return (
        <NavContext.Provider
            value={{
                currentRoute,
                setCurrentRoute,
                routeDisplayText,
                setRouteDisplayText,
                navExpanded,
                setNavExpanded
            }}>
            {children}
        </NavContext.Provider>
    )
}